import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    Grid,
    Hidden,
    Box,
    makeStyles,
    Tooltip,
    Button,
    Snackbar,
    Slide,
    Chip,
    Card,
    CardContent,
    TextField,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { Autocomplete } from '@material-ui/lab';
import { Alert } from '@material-ui/lab';
import {
    getAdminUsers,
    addAdminUser,
    deleteAdminUser,
    getUserDirectory
} from '../../../store/actions';
import { InfoDynamic } from '../../Common';

const AVAILABLE_SCOPES = ['admin:all', 'admin:basic'];

const useStyles = makeStyles((theme) => ({
    striped: {
        backgroundColor: '#EAEAEA',
        paddingTop: '.5em',
        paddingBottom: '.5em',
    },
    unstriped: {
        backgroundColor: '#fff',
        paddingTop: '.5em',
        paddingBottom: '.5em',
    },
    button: {
        margin: theme.spacing(.5),
        marginTop: theme.spacing(1.5),
    },
    root: {
        width: '100%'
    },
    content: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    scopeChip: {
        margin: theme.spacing(0.25),
    },
    addForm: {
        marginBottom: theme.spacing(2),
    }
}));

function SlideTransition(props) {
    return <Slide {...props} direction="down" />;
}

const AdminUsers = () => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const [searchTerm, updateSearchTerm] = useState();
    const { adminUsers, isLoading, error } = useSelector((state) => state.adminUsers, shallowEqual);
    const { users: directoryUsers } = useSelector((state) => state.userDirectory, shallowEqual);
    const [filteredUsers, updateFilteredUsers] = useState([]);
    const [addFormOpen, setAddFormOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedScopes, setSelectedScopes] = useState([]);
    const [resetConfirm, setResetConfirm] = useState({ open: false, user: null });

    const [snackbarState, updateSnackbarState] = useState({
        open: false,
        message: '',
        Transition: SlideTransition,
        severity: 'info'
    });

    useEffect(() => {
        dispatch(getAdminUsers());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (error) {
            updateSnackbarState({ ...snackbarState, open: true, message: error, severity: 'error' });
        } else {
            updateSnackbarState({ ...snackbarState, open: false, message: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);

    useEffect(() => {
        if (!adminUsers.length) {
            updateFilteredUsers([]);
            return;
        }
        if (!searchTerm) {
            updateFilteredUsers(adminUsers);
            return;
        }

        const searchToLower = searchTerm.toLowerCase();

        const filteredList = adminUsers.reduce((memo, user) => {
            try {
                if (Object.values(user).find(myValue => {
                    if (Array.isArray(myValue)) {
                        return myValue.some(v => typeof v === 'string' && v.toLowerCase().startsWith(searchToLower));
                    }
                    return (typeof myValue !== 'object' && typeof myValue !== 'undefined') && myValue.toString().toLowerCase().startsWith(searchToLower);
                })) {
                    return [...memo, user];
                } else {
                    return memo;
                }
            } catch (err) {
                return memo;
            }
        }, []);
        updateFilteredUsers(filteredList);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm, adminUsers]);

    const handleClose = () => {
        updateSnackbarState({ ...snackbarState, open: false });
    };

    const handleOpenAddForm = () => {
        dispatch(getUserDirectory());
        setAddFormOpen(true);
        setSelectedUser(null);
        setSelectedScopes([]);
    };

    const handleCancelAdd = () => {
        setAddFormOpen(false);
        setSelectedUser(null);
        setSelectedScopes([]);
    };

    const handleDeleteAdmin = (user) => {
        setResetConfirm({ open: true, user });
    };

    const handleConfirmReset = () => {
        dispatch(deleteAdminUser(resetConfirm.user._id));
        setResetConfirm({ open: false, user: null });
    };

    const handleCancelReset = () => {
        setResetConfirm({ open: false, user: null });
    };

    const handleSaveAdmin = () => {
        if (!selectedUser || !selectedScopes.length) {
            updateSnackbarState({ ...snackbarState, open: true, message: 'Please select a user and at least one scope.', severity: 'error' });
            return;
        }
        dispatch(addAdminUser(selectedUser.email, selectedScopes));
        setAddFormOpen(false);
        setSelectedUser(null);
        setSelectedScopes([]);
    };

    return (
        <div className={`${classes.root} fadeIn ${classes.content}`}>
            <Snackbar
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                autoHideDuration={5000}
                onClose={handleClose}
                TransitionComponent={snackbarState.Transition}
                open={snackbarState.open}
                message={snackbarState.message}
                key={snackbarState.Transition.name}
            >
                <Alert onClose={handleClose} severity={snackbarState.severity}>
                    {snackbarState.message}
                </Alert>
            </Snackbar>

            <Grid
                container
                spacing={2}
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
            >
                <h2>Admin Users</h2>

                <Grid item>
                    <Tooltip title="Add Admin User" aria-label="add admin">
                        <Button
                            size="small"
                            className='button-list'
                            variant='contained'
                            color="primary"
                            onClick={handleOpenAddForm}
                        ><AddIcon />
                        </Button>
                    </Tooltip>
                </Grid>
            </Grid>

            {addFormOpen && (
                <Card className={classes.addForm}>
                    <CardContent>
                        <Typography variant="subtitle1">Add Admin User</Typography>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item md={5} sm={12} xs={12}>
                                <Autocomplete
                                    value={selectedUser}
                                    id="admin-user-picker"
                                    fullWidth
                                    onChange={(event, newValue) => setSelectedUser(newValue)}
                                    options={directoryUsers}
                                    getOptionLabel={(option) => option.fullName || ''}
                                    renderOption={(option) => (
                                        <Box>
                                            <Typography variant="body2">{option.fullName}</Typography>
                                            <Typography variant="caption" color="textSecondary">{option.email}</Typography>
                                        </Box>
                                    )}
                                    renderInput={(params) => <TextField {...params} required label="Search Users" />}
                                />
                            </Grid>
                            <Grid item md={4} sm={12} xs={12}>
                                <Autocomplete
                                    multiple
                                    value={selectedScopes}
                                    id="scope-picker"
                                    fullWidth
                                    onChange={(event, newValue) => setSelectedScopes(newValue)}
                                    options={AVAILABLE_SCOPES}
                                    getOptionLabel={(option) => option}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip size="small" label={option} {...getTagProps({ index })} />
                                        ))
                                    }
                                    renderInput={(params) => <TextField {...params} required label="Scopes" />}
                                />
                            </Grid>
                            <Grid item md={3} sm={12} xs={12}>
                                <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleSaveAdmin}
                                    style={{ marginRight: 8 }}
                                >Save</Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={handleCancelAdd}
                                >Cancel</Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}

            <Grid
                container
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={3}
            >
                <Grid item md={5} sm={12} xs={12}>
                    <InfoDynamic
                        fieldType='text'
                        header='Search'
                        fieldName='searchTerm'
                        fieldValue={searchTerm}
                        editMode={true}
                        handleChange={(newSearchTerm) => updateSearchTerm(newSearchTerm.searchTerm)}
                    />
                </Grid>
            </Grid>

            <Box p={1} />
            <Table size="small" aria-label="list of admin users" style={isLoading ? { 'display': 'none' } : {}}>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Typography variant="overline">Name</Typography>
                        </TableCell>
                        <Hidden xsDown>
                            <TableCell>
                                <Typography variant="overline">Email</Typography>
                            </TableCell>
                        </Hidden>
                        <TableCell>
                            <Typography variant="overline">Scopes</Typography>
                        </TableCell>
                        <TableCell />
                    </TableRow>
                </TableHead>

                <TableBody>
                    {filteredUsers.map((user, index) => (
                        <TableRow key={index} className={(index % 2 === 0) ? classes.unstriped : classes.striped}>
                            <TableCell>
                                <Typography variant="caption">{user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim()}</Typography>
                            </TableCell>
                            <Hidden xsDown>
                                <TableCell>
                                    <Typography variant="caption">{user.email}</Typography>
                                </TableCell>
                            </Hidden>
                            <TableCell>
                                {(user.scope || []).map((scope, i) => (
                                    <Chip
                                        key={i}
                                        size="small"
                                        label={scope}
                                        className={classes.scopeChip}
                                    />
                                ))}
                            </TableCell>
                            <TableCell align="right">
                                <Tooltip title="Reset to default permissions">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteAdmin(user)}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={resetConfirm.open} onClose={handleCancelReset}>
                <DialogTitle>Reset Permissions</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to reset <strong>{resetConfirm.user?.fullName || resetConfirm.user?.email}</strong> to default permissions? Their scope will be set to <Chip size="small" label="user:read" />.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelReset}>Cancel</Button>
                    <Button onClick={handleConfirmReset} color="secondary" variant="contained">
                        Reset
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export {
    AdminUsers
};
